import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  FC,
} from "react";
import { signOut, User } from "firebase/auth";
import { useMatch, useNavigate } from "react-router";

import { serializeModel } from "../../api";
import { auth, onAuthStateChanged } from "../../firebase";
import {
  useAppDispatch,
  useAppSelector,
  setClaims,
  signIn,
  signOut as signOutAction,
} from "../../store";
import { PureVoidFunc } from "../../helpers/functions/types";
import { CHAT_HISTORY_KEY } from "../../ui/chat/storageKeys";
import { safeSessionStorage } from "../../helpers/browser/safeSessionStorage";
import { useIsSelfManagedAuthRoute } from "./useIsSelfManagedAuthRoute";

type AuthContextT = {
  user: User | null;
  logout: PureVoidFunc;
  getToken: () => Promise<string>;
  linkBeforeAuth: string | null;
  resetLinkBeforeAuth: PureVoidFunc;
};

const AuthContext = createContext<AuthContextT>({
  user: null,
  logout: () => {},
  getToken: () => Promise.reject(),
  linkBeforeAuth: null,
  resetLinkBeforeAuth: () => {},
});

export const AuthProvider: FC<React.PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [linkBeforeAuth, setLinkBeforeAuth] = useState<null | string>(null);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const isRootRoute = !!useMatch("/");
  const isAuthRoute = !!useMatch("/auth/*");
  const isRegister = !!useMatch("/auth/register");
  const isNoCompany = !!useMatch("/auth/noCompany");
  const isResetPassword = !!useMatch("/auth/resetPassword");
  const isSelfManagedAuthRoute = useIsSelfManagedAuthRoute();

  const needToResetStorage = useAppSelector(
    (state) => state.panel.needToResetStorage,
  );

  useEffect(() => {
    onAuthStateChanged(auth, async (userAuth) => {
      if (needToResetStorage) return;

      setUser(userAuth);

      if (userAuth === null) {
        dispatch(signOutAction());
        if (!isRootRoute && !isAuthRoute) {
          setLinkBeforeAuth(window.location.pathname);
        }
        if (isRegister) {
          navigate("/auth/register");
        } else if (
          !isResetPassword &&
          !isNoCompany &&
          !isSelfManagedAuthRoute
        ) {
          navigate("/auth/signin");
        }
        return;
      }

      const user = serializeModel<User>(userAuth);
      dispatch(signIn(user));
      const token = await userAuth?.getIdTokenResult();
      dispatch(setClaims(token));
    });
  }, [
    needToResetStorage,
    dispatch,
    isAuthRoute,
    isRegister,
    isRootRoute,
    navigate,
    isResetPassword,
    isNoCompany,
    isSelfManagedAuthRoute,
  ]);

  const value = useMemo(() => {
    const logout = async () => {
      setUser(null);
      safeSessionStorage.remove(CHAT_HISTORY_KEY);
      await signOut(auth);
      navigate("/auth/signin", { replace: true });
    };

    const resetLinkBeforeAuth = async () => {
      setLinkBeforeAuth(null);
    };

    const getToken = async () => {
      if (!user)
        return Promise.reject("AuthProvider doesn't contain user data");
      return await user.getIdToken();
    };

    return {
      user,
      logout,
      getToken,
      linkBeforeAuth,
      resetLinkBeforeAuth,
    };
  }, [user, navigate, linkBeforeAuth]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
